import { useState, useEffect } from "react";

// Helper: Generate a deterministic HSL color based on string input
const getHashColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return {
    bg: `hsl(${hue}, 65%, 85%)`,
    text: `hsl(${hue}, 70%, 25%)`,
  };
};

// Extract 2-character initials safely from a name string
const getInitials = (name = "") => {
  if (!name.trim()) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Fallback Initials Avatar Component
const CustomAvatar = ({ name = "", className = "" }) => {
  const initials = getInitials(name);
  const { bg, text } = getHashColor(name);

  return (
    <div
      className={`flex items-center justify-center rounded-full font-bold uppercase select-none ${className}`}
      style={{ backgroundColor: bg, color: text }}
    >
      <span className="tracking-wider">{initials}</span>
    </div>
  );
};

// Main Reusable Dual-Avatar Component
const Avatar = ({
  name = "",
  subName = "",
  logo = "",
  subLogo = "",
  className = "",
  sizeClass = "h-10 w-10",
  subSizeClass = "h-5 w-5 text-[11px]",
  avatarImageClass = "",
  avatarSubImageClass = "",
}) => {
  const [imgError, setImgError] = useState(false);
  const [subImgError, setSubImgError] = useState(false);

  // Reset error states when logo URLs change dynamically
  useEffect(() => setImgError(false), [logo]);
  useEffect(() => setSubImgError(false), [subLogo]);

  const showMainLogo = Boolean(logo) && !imgError;
  const showSubLogo = Boolean(subLogo) && !subImgError;
  const hasSubAvatar = Boolean(subLogo || subName);

  return (
    <div className={`relative inline-block shrink-0 ${className}`}>
      {/* Primary Avatar */}
      {showMainLogo ? (
        <img
          src={logo}
          alt={name || "Avatar"}
          onError={() => setImgError(true)}
          className={`${sizeClass} rounded-full bg-gray-100 object-contain ${avatarImageClass}`}
        />
      ) : (
        <CustomAvatar name={name} className={sizeClass} />
      )}

      {/* Sub Avatar / Badge Overlay */}
      {hasSubAvatar && (
        <div className="absolute -bottom-1 -right-1">
          {showSubLogo ? (
            <img
              src={subLogo}
              alt={subName || "Sub Avatar"}
              onError={() => setSubImgError(true)}
              className={`${subSizeClass} rounded-full border-[1px] border-white bg-gray-100 object-contain shadow-sm ${avatarSubImageClass}`}
            />
          ) : (
            <CustomAvatar
              name={subName}
              className={`${subSizeClass} border-[1px] border-white shadow-sm`}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Avatar;